import Cookies from "js-cookie";
import axios from 'axios';

// Same-origin in production; CRA's dev proxy forwards /api to the Express server locally.
export const instance = axios.create({
    baseURL: '/api'
});

// Attach the JWT to every request.
instance.interceptors.request.use(config => {
    const token = Cookies.get("jwt");
    if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

export const forgotPassword = async (email) => {
    return await instance.post('/auth/forgot-password', { email })
}

export const resetPassword = async (code, password, passwordConfirmation) => {
    return await instance.post('/auth/reset-password', {
        password: password,
        passwordConfirmation: passwordConfirmation,
        code: code
    })
}

// Creates the TNP account (and the LightSpeed VT account, server-side). Returns { jwt, user }.
export const createUser = async (data) => {
    const response = await instance.post('/auth/register', {
        firstname: data.firstname,
        lastname: data.lastname,
        username: data.username,
        email: data.email,
        password: data.password,
        bootcampId: data.bootcampId,
    });
    return response.data;
}

// Bootcamps open for registration.
export const getOpenBootcamps = async () => {
    const response = await instance.get('/auth/bootcamps');
    return response.data;
}

export const isUsernameAvailable = async (username) => {
    const response = await instance.get('/auth/username-available', { params: { username } });
    return response.data.available;
}

const customerPayload = (data) => ({
    name: data.name,
    qc: data.qc,
    appset: data.appset,
    appshown: data.appshown,
    contacted: data.contacted,
    sold: data.sold,
    referrals: data.referrals,
});

// The server assigns the user, bootcamp and team color. An exact duplicate of one of your
// customers is refused (409) unless allowDuplicate is set.
export const createCustomer = async (data, allowDuplicate = false) => {
    return await instance.post('/customers', { ...customerPayload(data), allowDuplicate })
}

// Your customers this bootcamp with the same or a similar name: [{ match: 'exact'|'similar', customer }].
export const checkDuplicateCustomer = async (name) => {
    const result = await instance.get('/customers/check-duplicate', { params: { name } });
    return result.data.matches;
}

export const updateCustomer = async (id, data) => {
    return await instance.put(`/customers/${id}`, customerPayload(data))
}

export const getCustomersByUser = async () => {
    const req = await instance.get('/customers/mine');
    return req.data;
}

export const getCustomersByBootcamp = async () => {
    const req = await instance.get('/bootcamp/customers');
    return req.data;
}

export const getDealerships = async () => {
    const result = await instance.get('/dealerships');
    return result.data;
}

// Returns { user, bootcamp, bootcampName, team } for the logged-in user.
// Pass a token right after login, before it's stored in the cookie.
export const getCurrentCamp = async (token) => {
    const result = await instance.get('/auth/me', token ? {
        headers: { Authorization: `Bearer ${token}` }
    } : undefined);
    return result.data;
}

// Team rosters for the active bootcamp: { redTeam, greenTeam, blackTeam, blueTeam }.
export const getUsers = async () => {
    try {
        const result = await instance.get('/bootcamp/teams');
        return result.data;
    } catch (error) {
        console.log('An error occurred:', error);
    }
}

export const getUsersByDealership = async (dealershipID) => {
    try {
        const result = await instance.get(`/dealerships/${dealershipID}/users`);
        return result.data;
    } catch (error) {
        console.log('An error occurred:', error);
    }
}

// { loginUrl, tracking, linked, username, extension, calls: { today, bootcamp } | null }
export const getTelzioMe = async () => {
    const result = await instance.get('/telzio/me');
    return result.data;
}

// Latest rep activity in your bootcamp, newest first: [{ id, teamColor, kind, count, points, at, rep }]
export const getBootcampActivity = async (limit = 3) => {
    const result = await instance.get('/bootcamp/activity', { params: { limit } });
    return result.data;
}

// { active, sprint, lastSprint } for your bootcamp's race.
export const getRaceState = async () => (await instance.get('/race')).data;

// A pit stop nudge for you, or null.
export const getPitStop = async () => (await instance.get('/pitstop')).data;

// Your numbers in the running sprint ({ sprint, mine, team }), or null.
export const getMySprint = async () => (await instance.get('/sprint/me')).data;

// Pit stop nudges from Race control.
export const getCurrentNudge = async () => (await instance.get('/nudges/current')).data;
export const markNudgeSeen = async (id) => instance.post(`/nudges/${id}/seen`);
export const dismissNudge = async (id) => instance.post(`/nudges/${id}/dismiss`);
